import { useEffect, memo, useMemo, useState } from 'react';
import { Common } from '@stockbit/ui';
import { Form, FormProps } from 'antd';

// components
import If from 'global/ErrorHandlers/If';
import InputStepper from 'global/Securities/components/InputStepper';
import OrderTypeFormItem from 'global/Securities/components/Form/TransactionForm/OrderTypeFormItem';
import SellLeverage from 'features/day-trade/components/SellLeverage/sell-leverage';
import Slider from 'global/Securities/components/Slider';
import SpecialMonitoringBanner from 'global/TradingModal/components/SpecialMonitoringBanner';
import SplitOrderFormItem from 'global/Securities/components/Form/TransactionForm/SplitOrderFormItem';

// hooks
import useEnableSplitOrder from 'global/TradingModal/hooks/useEnableSplitOrder';
import useGetPortfolioData from 'hooks/useGetPortfolioData';

// libs
import tradingDataStore from 'lib/stores/tradingData';
import { VIRTUAL_TRADING_TYPES } from 'lib/analytics/virtualTrading';

import {
  clevertapOrderPlaced,
  clevertapOrderQuantityAdjusted,
  clevertapSellOrderViewed,
} from 'lib/analytics/securities';

// stores
import realTradingStore from 'global/TradingModal/RealTradingModal/store';
import tradingModalStore from 'global/TradingModal/store';
import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';

// utils
import { formatNumber, formatPercentage } from 'utils/number';
import { getColorBasedOnNumber } from 'utils/general';
import { getLotOrderLimit } from 'global/TradingModal/RealTradingModal/utils';
import { getStepFractionByValue } from 'global/Securities/utils/transactionPrice';
import {
  getBuyAmountInvested,
  getProfitLoss,
  getSellAmountInvestedFee,
  getSellGain,
} from 'global/Securities/utils/formula';

// constants
import featureFlags from 'constants/featureFlags';
import { expiryOptions } from 'global/Securities/constants/transactionForm';
import { lotSize, DATA_CY_MODAL_TRADING } from 'global/TradingModal/constant';
import {
  CorporateActionStockType,
  ORDER_TYPE,
  STOCK_TYPE,
} from 'constants/trading';

// types
import { LEVERAGE } from 'global/TradingModal/RealTradingModal/types';
import {
  SellOrderType,
  TransactionType,
  Expiry,
  SplitOrderMethodEnum,
} from 'global/Securities/types/transaction';
import {
  TradingFeatureEnum,
  EventTypeEnum,
} from 'global/Securities/types/trading';

import { ButtonWrapper, OrderButton } from 'global/TradingModal/styled';

// styles
import { FieldsWrapper, SliderPercentText, TransactionForm } from './styled';

const { Select, Text, Flex } = Common;

const calculateSliderPercentage = (lotTransaction, maxLot) => {
  // Odd lot need to round down
  const lotAvailabletoSell = Math.floor(maxLot);
  const sliderValue = lotTransaction / lotAvailabletoSell;

  const percentage = !Number.isNaN(sliderValue)
    ? Math.round(sliderValue * 100)
    : 0;

  if (percentage === Infinity) return 0;
  if (percentage > 100) return 100;

  return percentage;
};

const calculateMaxLotInput = (
  price,
  previous_sell_price,
  available_lot,
  original_available_lot = 0,
  isAmend,
) => {
  const isUserDoAmend = isAmend && previous_sell_price !== price;

  const maxLotToAmend = Math.floor(available_lot + original_available_lot);
  const maxLotToSell = Math.floor(original_available_lot);

  return isUserDoAmend ? maxLotToAmend : maxLotToSell;
};

interface FormSellProps {
  showSpecialMonitoringModal: () => void;
  onSubmit: () => void;
  onShowSplitOrderInfo?: () => void;
  onShowDayTradeUnavailable?(): void;
  onShowSplitOrderMethodInfo?(): void;
}

const FormSell = ({
  showSpecialMonitoringModal,
  onShowSplitOrderInfo = () => {},
  onShowSplitOrderMethodInfo = () => {},
  onSubmit,
  onShowDayTradeUnavailable = () => {},
}: FormSellProps) => {
  const [form] = Form.useForm();
  const [hasFormError, setHasFormError] = useState(false);

  const isShowSplitOrder = useEnableSplitOrder('sell');

  const {
    company: {
      data: {
        market_hour: marketHour,
        type_company: productType,
        day_trade_info: dayTradeInfo,
      },
    },
    portfolioDetailDayTrade: { data: detailDayTrade },
    getCashOnHand,
  } = tradingDataStore();

  const loadTradingInfo = useTradingInfoStore((s) => s.loadTradingInfo);
  const splitOrder = useTradingInfoStore((s) => s.splitOrder);
  const { max_order: maxOrder } = splitOrder || {};

  const { multiplier } = dayTradeInfo || {};

  const { orderType, orderedPrice, orderedLot, symbol, isDayTrade } =
    tradingModalStore();

  const {
    price,
    shares,
    expiry,
    type: sellOrderType,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    splitMethod,
    sellLeverage: leverage,
    setPrice,
    setShares,
    setExpiry,
    setOrderType,
    setSplitOrderActive,
    setSplitQuantity,
    setSplitRange,
    setSplitMethod,
    setSellLeverage: setLeverage,
    setDayTradeActive,
    setDayTadeMultiplier,
  } = realTradingStore();
  const getPortfolioDetail = useGetPortfolioData(symbol);

  const isDayTradeActive = leverage === LEVERAGE.DAY_TRADE;
  const isAmend = orderType === ORDER_TYPE.AMEND_SELL;
  const isSaham = productType.toLowerCase() === STOCK_TYPE.saham;
  const isRight = productType.toLowerCase() === STOCK_TYPE.right;

  const isCorporateAction = useMemo(
    () => CorporateActionStockType.includes(productType.toLowerCase()),
    [productType],
  );

  const hideExpiry = useMemo(
    () => !isSaham || isAmend || sellOrderType !== SellOrderType.LimitOrder,
    [isSaham, isAmend, sellOrderType],
  );

  const showOrderType = useMemo(
    () => isSaham && sellOrderType,
    [isSaham, sellOrderType],
  );

  const {
    available_lot: availableLot = 0,
    price_average_fee: priceAverageFee = 0,
  } = getPortfolioDetail;
  const {
    available_lot: availableLotDayTrade = 0,
    price_average_fee: priceAverageFeeDayTrade = 0,
  } = detailDayTrade;

  // Get available lot and price average fee based on user day trade or user regular
  const [available_lot, price_average_fee] = useMemo(() => {
    const RegularSell = [availableLot, priceAverageFee];
    const DayTradeSell = [availableLotDayTrade, priceAverageFeeDayTrade];

    // If order is amend, return based on order type, if not return based on user day trade
    // isDayTrade is true if user click amend button from order which is day trade
    if (isAmend) {
      return isDayTrade ? DayTradeSell : RegularSell;
    }

    // If order is not amend, return based on user day trade
    const regularLotAvailable = availableLot > 0;
    const dayTradeLotAvailable = availableLotDayTrade > 0;

    // If user have both regular and day trade lot, leverage will not selected
    if (!leverage) {
      // user have both regular and day trade lot, return 0 so user have to select leverage
      if (regularLotAvailable && dayTradeLotAvailable) {
        return [0, 0];
      }
    }

    // If user have day trade lot and leverage is selected, return day trade sell
    // Else return regular sell
    return isDayTradeActive && dayTradeLotAvailable
      ? DayTradeSell
      : RegularSell;
  }, [
    availableLot,
    priceAverageFee,
    availableLotDayTrade,
    priceAverageFeeDayTrade,
    isAmend,
    isDayTrade,
    leverage,
    isDayTradeActive,
  ]);

  // originalAvailableLot is used to calculate max lot input
  // If user is day trade and not amend, available lot is based on day trade
  const originalAvailableLot = useMemo(() => {
    if (!isAmend) {
      return available_lot;
    }

    return orderedLot || available_lot;
  }, [isAmend, available_lot, orderedLot]);

  // availableLotToSell is used to show available lot to sell
  const availableLotToSell = useMemo(() => {
    // Check if user is day trade and not amend, return day trade available lot if available
    if (!isAmend && !leverage && availableLotDayTrade > 0) {
      return availableLot <= 0 ? availableLotDayTrade : '-';
    }

    return isAmend ? available_lot + orderedLot : available_lot;
  }, [
    availableLot,
    availableLotDayTrade,
    isAmend,
    leverage,
    available_lot,
    orderedLot,
  ]);

  // isDayTradeSell is used to check if user is day trade and sell order is active
  // isDayTrade is true if user click amend button from order which is day trade (amend)
  // isDayTradeActive is true if user have day trade lot and leverage is selected (new order)
  const isDayTradeSell = useMemo(
    () => (isAmend ? isDayTrade : isDayTradeActive),
    [isAmend, isDayTrade, isDayTradeActive],
  );

  // isLeverageEnabled is used to show sell leverage component
  const isLeverageEnabled = useMemo(() => {
    // If amend regular order, return false
    if (isAmend && !isDayTrade) {
      return false;
    }

    // If order is amend and day trade, return true
    if (isAmend && isDayTrade) {
      return true;
    }

    // If user have day trade lot and leverage is not selected, return true
    return availableLotDayTrade > 0;
  }, [isAmend, isDayTrade, availableLotDayTrade]);

  // isDisabledInput is used to disable input if have day trade lot and leverage is not selected
  const isDisabledInput = useMemo(
    () => !isAmend && !leverage && availableLotDayTrade > 0,
    [isAmend, leverage, availableLotDayTrade, availableLot],
  );

  const lot = useMemo(() => {
    const lotOrderLimit = getLotOrderLimit();
    const userLot =
      typeof shares === 'number' ? Math.floor(shares / lotSize) : available_lot;

    return Math.floor(userLot > lotOrderLimit ? lotOrderLimit : userLot);
  }, [shares, lotSize, available_lot]);

  const isDisabledSellSplitOrder = useMemo(() => {
    if (!isSplitOrderActive) {
      return false;
    }

    if (splitQuantity === null || splitQuantity < 1) {
      return true;
    }

    if (
      splitMethod === SplitOrderMethodEnum.RANDOM &&
      (!splitRangeMin || !splitRangeMax)
    ) {
      return true;
    }

    return false;
  }, [
    isSplitOrderActive,
    splitQuantity,
    splitMethod,
    splitRangeMin,
    splitRangeMax,
  ]);

  const isSubmitDisabled = useMemo(
    () => lot < 1 || price === 0 || hasFormError || isDisabledSellSplitOrder,
    [lot, price, hasFormError, isSplitOrderActive, isDisabledSellSplitOrder],
  );

  const showSplitOrderForm = useMemo(
    () => isShowSplitOrder && !isDayTradeActive,
    [isShowSplitOrder, isDayTradeActive],
  );

  const maxLot = calculateMaxLotInput(
    price,
    orderedPrice,
    available_lot,
    originalAvailableLot,
    isAmend,
  );

  const lotPercentage = calculateSliderPercentage(lot, maxLot);

  const sellAmountInvestedFee = getSellAmountInvestedFee(
    lot,
    price,
    isCorporateAction,
    isDayTradeSell,
  );

  const buyAmountInvestedAveragePriceFee = getBuyAmountInvested(
    lot,
    price_average_fee,
  );

  const sellProfitLoss = getProfitLoss(
    sellAmountInvestedFee,
    buyAmountInvestedAveragePriceFee,
  );

  const sellGain = getSellGain(
    sellProfitLoss,
    buyAmountInvestedAveragePriceFee,
  );

  const getMaxLot = () => {
    const lotOrderLimit = getLotOrderLimit();
    return maxLot > lotOrderLimit ? lotOrderLimit : maxLot;
  };

  const handleValidateAllFields = () => {
    form.validateFields();
  };

  const handleGtcChange = (selectedExpiry: Expiry) => {
    setExpiry(selectedExpiry);

    if (selectedExpiry === Expiry.GoodForDay) {
      setSplitOrderActive(false);
      form.setFieldsValue({ isSplitOrderActive: false });
    }
  };

  const handlePriceChange = (newPrice: number) => {
    clevertapOrderQuantityAdjusted({
      symbol,
      orderType,
      adjustedType: 'add/substract',
    });
    setPrice(newPrice);
    handleValidateAllFields();
  };

  const handleLotChange = (newLot: number) => {
    clevertapOrderQuantityAdjusted({
      symbol,
      orderType,
      adjustedType: 'add/substract',
    });
    const roundLot = Math.floor(newLot);
    setShares(roundLot * lotSize);
    handleValidateAllFields();
  };

  const handleOrderTypeChange = (type: SellOrderType) => {
    setOrderType(type);
  };

  const handleFieldsChange: FormProps['onFieldsChange'] = (_, allFields) => {
    const hasEmpty = allFields.some(({ value }) => value === undefined);
    const hasError = allFields.some(({ errors }) => errors.length > 0);
    setHasFormError(hasEmpty || hasError);
  };

  const handleSaveFields = () => {
    const values = form.getFieldsValue();
    if (isShowSplitOrder && !isDayTradeActive) {
      setSplitOrderActive(!!values.isSplitOrderActive);
      setSplitQuantity(values.splitQuantity);
      setSplitRange(values.splitRangeMin, values.splitRangeMax);
    } else {
      setSplitOrderActive(false);
      setSplitQuantity(null);
      setSplitRange(null, null);
    }

    setDayTradeActive(isDayTradeSell);
    setDayTadeMultiplier(isAmend && isDayTrade ? parseInt(multiplier, 10) : 0);
  };

  const handleSubmit: FormProps['onFinish'] = async () => {
    handleSaveFields();

    if (isDayTradeSell) {
      const { day_trade } = await loadTradingInfo([
        TradingFeatureEnum.DAY_TRADE,
      ]);

      // only check if day trade response is available
      if (day_trade) {
        const isTimeLimit = day_trade.remaining_trading_close <= 0;

        const isUnavailable = [
          EventTypeEnum.EXCEED_DEBT_RATIO,
          EventTypeEnum.OUTSIDE_MARKET_HOURS,
        ].includes(day_trade?.event_type);

        // show day trade unavailable if day trade is not allowed
        if (isUnavailable || isTimeLimit) {
          onShowDayTradeUnavailable();
          return;
        }
      }
    }

    onSubmit();
    clevertapOrderPlaced({
      orderType,
      symbol,
      price,
      lot,
      gainLoss: `${sellGain}%`,
      totalLot: `${lotPercentage}%`,
      status: isSubmitDisabled ? 'failed' : 'success',
      expiry: expiryOptions[expiry],
      type: VIRTUAL_TRADING_TYPES.realTrading,
      dayTrade: isDayTradeSell,
    });
  };

  const handleChangeSwitchSplitOrder = (checked: boolean) => {
    if (!checked) {
      setSplitMethod(SplitOrderMethodEnum.RANDOM);
    }
  };

  useEffect(() => {
    if (isRight) getCashOnHand();

    clevertapSellOrderViewed(symbol);
  }, [isRight]);

  useEffect(() => {
    if (isAmend) {
      setShares(orderedLot * lotSize);
    } else {
      setShares(Math.floor(available_lot) * lotSize);
    }
  }, [isAmend, orderedLot, available_lot]);

  useEffect(() => {
    if (!sellOrderType) setOrderType(SellOrderType.LimitOrder);
  }, [sellOrderType, setOrderType]);

  useEffect(() => {
    if (isDayTradeActive) {
      setExpiry(Expiry.GoodForDay);
      setSplitOrderActive(false);
      setSplitQuantity(null);
      setSplitRange(null, null);
    }

    if (!leverage) {
      if (detailDayTrade.available_lot > 0 && availableLot <= 0) {
        setLeverage(LEVERAGE.DAY_TRADE);
        setDayTradeActive(true);
      }

      if (detailDayTrade.available_lot <= 0 && availableLot > 0) {
        setLeverage(LEVERAGE.REGULAR);
        setDayTradeActive(false);
      }
    }
  }, [leverage]);

  return (
    <>
      <If condition={marketHour.suspend_info}>
        <SpecialMonitoringBanner
          onClick={showSpecialMonitoringModal}
          title={marketHour.suspend_info}
        />
      </If>

      <TransactionForm
        form={form}
        colon={false}
        labelCol={{ span: 12 }}
        onFinish={handleSubmit}
        onFieldsChange={handleFieldsChange}
        onBlur={handleSaveFields}
        onValuesChange={handleSaveFields}
      >
        <If condition={showOrderType}>
          <OrderTypeFormItem
            form={form}
            transactionType={TransactionType.Sell}
            defaultValue={sellOrderType as SellOrderType}
            onChange={handleOrderTypeChange}
            hideInfoIcon
          />
        </If>

        <FieldsWrapper>
          <If condition={isLeverageEnabled}>
            <Form.Item label="Leverage">
              <SellLeverage
                dayTradeLot={
                  isAmend ? availableLotToSell : detailDayTrade.available_lot
                }
                regularLot={getPortfolioDetail.available_lot}
                isAmend={isAmend}
                leverage={leverage}
                handleChangeLeverage={(value) => {
                  setLeverage(value);
                  setDayTradeActive(value === LEVERAGE.DAY_TRADE);

                  if (value === LEVERAGE.DAY_TRADE && isSplitOrderActive) {
                    setSplitOrderActive(false);
                    form.setFieldsValue({ isSplitOrderActive: false });
                  }
                }}
              />
            </Form.Item>
          </If>

          <Form.Item label="Available Lot">
            <Text
              align="right"
              data-cy={DATA_CY_MODAL_TRADING.AVAILABLE_LOT_VALUE}
            >
              {availableLotToSell}
            </Text>
          </Form.Item>

          <Form.Item className="slider-form-item" label="Sell Order Lot">
            <Flex align="center">
              <Slider
                min={0}
                max={getMaxLot()}
                value={lot}
                tipFormatter={null}
                disabled={isDisabledInput}
                onChange={(newLot) => {
                  clevertapOrderQuantityAdjusted({
                    symbol,
                    orderType,
                    adjustedType: 'slider',
                  });
                  setShares(Math.floor(newLot) * lotSize);
                  handleValidateAllFields();
                }}
              />

              <SliderPercentText>{lotPercentage}%</SliderPercentText>
            </Flex>
          </Form.Item>

          <Form.Item
            label=""
            wrapperCol={{
              xs: { offset: 0 },
              sm: { span: 12, offset: 12 },
            }}
            data-cy={DATA_CY_MODAL_TRADING.FORM_SELL_ORDER_LOT}
          >
            <InputStepper
              containerStyle={{ width: 160 }}
              defaultValue={lot}
              value={lot}
              min={0}
              max={getMaxLot()}
              step={1}
              onChange={handleLotChange}
              disabled={isDisabledInput}
            />
          </Form.Item>

          <Form.Item label="Price" data-cy={DATA_CY_MODAL_TRADING.FORM_PRICE}>
            <InputStepper
              containerStyle={{ width: 160 }}
              value={price}
              min={0}
              step={getStepFractionByValue(price)}
              onChange={handlePriceChange}
              disableDecimal
              disabled={isDisabledInput}
            />
          </Form.Item>

          <Form.Item label="Proceeds (Net Fee)">
            <Text family="bold" align="right">
              Rp
              {formatNumber(sellAmountInvestedFee, false)}
            </Text>
          </Form.Item>

          <Form.Item label="Profit/Loss">
            <Text
              align="right"
              color={getColorBasedOnNumber(sellProfitLoss)}
              data-cy={DATA_CY_MODAL_TRADING.PROFIT_LOSS_VALUE}
            >
              {formatNumber(sellProfitLoss, false)} ({sellProfitLoss > 0 && '+'}
              {sellGain === Infinity || sellGain === 0
                ? '0.00%'
                : formatPercentage(sellGain, false)}
              )
            </Text>
          </Form.Item>

          <If condition={!hideExpiry}>
            <Form.Item label="Expiry">
              <Select
                value={expiry}
                width="100%"
                onChange={handleGtcChange}
                disabled={isDisabledInput || isDayTradeActive}
                data-cy={DATA_CY_MODAL_TRADING.EXPIRY_SELECT}
                getPopupContainer={() =>
                  document.getElementById(
                    DATA_CY_MODAL_TRADING.EXPIRY_SELECT_DROPDOWN,
                  )
                }
              >
                {Object.entries(expiryOptions).map(([key, value]) => (
                  <Select.Option key={key} value={key}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div
              id={DATA_CY_MODAL_TRADING.EXPIRY_SELECT_DROPDOWN}
              data-cy={DATA_CY_MODAL_TRADING.EXPIRY_SELECT_DROPDOWN}
            />
          </If>

          <If condition={showSplitOrderForm}>
            <SplitOrderFormItem
              form={form}
              orderLot={lot}
              checkedInitialValue={isSplitOrderActive}
              quantityInitialValue={splitQuantity}
              minRangeInitialValue={splitRangeMin}
              maxRangeInitialValue={splitRangeMax}
              maxQuantity={maxOrder}
              transactionType={TransactionType.Sell}
              onInfoIconClick={onShowSplitOrderInfo}
              disabledToggle={isDisabledInput}
              onSwitchChange={handleChangeSwitchSplitOrder}
              onSplitMethodInfoClick={onShowSplitOrderMethodInfo}
            />
          </If>
        </FieldsWrapper>

        <ButtonWrapper>
          <OrderButton
            htmlType="submit"
            disabled={isSubmitDisabled}
            type="secondary"
            radius="4px"
            block
            height="40px"
            data-cy={DATA_CY_MODAL_TRADING.BUTTON_PLACE_ORDER}
          >
            <Text>Place Order</Text>
          </OrderButton>
        </ButtonWrapper>
      </TransactionForm>
    </>
  );
};

export default memo(FormSell);
